import React, { useState, useEffect, useCallback } from 'react';
import {
  Download, RefreshCw, CheckCircle, XCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import api from '../../services/api';

const ENTITIES = ['Eyvo USA', 'Eyvo India'];
const TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'cogs', 'expense'];

const TYPE_STYLES = {
  asset:     'bg-blue-50   text-blue-700',
  liability: 'bg-red-50    text-red-700',
  equity:    'bg-purple-50 text-purple-700',
  revenue:   'bg-green-50  text-green-700',
  cogs:      'bg-orange-50 text-orange-700',
  expense:   'bg-slate-100 text-slate-600',
};

const fmt = (n) =>
  n === 0 ? '' : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

function today() {
  return new Date().toISOString().slice(0, 10);
}
function fyStart() {
  return `${new Date().getFullYear()}-01-01`;
}

export default function TrialBalance() {
  const [entity, setEntity]       = useState('Eyvo USA');
  const [dateFrom, setDateFrom]   = useState(fyStart());
  const [dateTo, setDateTo]       = useState(today());
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [expandedTypes, setExpandedTypes] = useState(new Set(TYPE_ORDER));

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/api/accounts/reports/trial-balance', {
        params: { entity, date_from: dateFrom, date_to: dateTo },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load trial balance');
    } finally {
      setLoading(false);
    }
  }, [entity, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const toggleType = (type) => {
    setExpandedTypes(prev => {
      const s = new Set(prev);
      s.has(type) ? s.delete(type) : s.add(type);
      return s;
    });
  };

  const downloadCSV = () => {
    if (!data?.lines?.length) return;
    const headers = ['Account Code', 'Account Name', 'Type', 'Gross Debits', 'Gross Credits', 'Net Debit', 'Net Credit'];
    const rows = data.lines.map(l => [
      l.account_code, `"${l.account_name}"`, l.account_type,
      l.gross_debits, l.gross_credits, l.net_dr, l.net_cr,
    ]);
    rows.push(['', 'TOTAL', '', data.total_gross_dr, data.total_gross_cr, data.total_net_dr, data.total_net_cr]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `trial_balance_${entity.replace(' ', '_')}_${dateFrom}_to_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Group lines by account_type
  const grouped = data
    ? TYPE_ORDER.reduce((acc, type) => {
        const lines = data.lines.filter(l => l.account_type === type);
        if (lines.length) acc[type] = lines;
        return acc;
      }, {})
    : {};

  const typeLabels = {
    asset: 'Assets', liability: 'Liabilities', equity: 'Equity',
    revenue: 'Revenue', cogs: 'Cost of Goods Sold', expense: 'Operating Expenses',
  };

  return (
    <div className="space-y-4" data-testid="trial-balance-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Trial Balance</h1>
          <p className="text-sm text-slate-500 mt-0.5">All accounts with gross debits, gross credits, and net balances</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50"
            data-testid="refresh-trial-balance-btn"
          >
            <RefreshCw size={14} className={`text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={downloadCSV}
            disabled={!data?.lines?.length}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-700 text-sm rounded-md hover:bg-slate-50 disabled:opacity-40"
            data-testid="download-trial-balance-btn"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Entity</label>
          <select
            value={entity}
            onChange={e => setEntity(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-900"
            data-testid="entity-select"
          >
            {ENTITIES.map(e => <option key={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none"
            data-testid="date-from-input"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none"
            data-testid="date-to-input"
          />
        </div>
      </div>

      {/* Balance check banner */}
      {data && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
          ${data.is_balanced ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
          data-testid="balance-check-banner"
        >
          {data.is_balanced
            ? <><CheckCircle size={15} /> Ledger is balanced — Total Dr = Total Cr = {fmtMoney(data.total_gross_dr)}</>
            : <><XCircle    size={15} /> Ledger is NOT balanced — Dr {fmtMoney(data.total_gross_dr)} ≠ Cr {fmtMoney(data.total_gross_cr)}</>
          }
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      )}

      {/* Table by account type */}
      {data && Object.entries(grouped).map(([type, lines]) => {
        const isOpen     = expandedTypes.has(type);
        const typeDr     = lines.reduce((s, l) => s + l.gross_debits,  0);
        const typeCr     = lines.reduce((s, l) => s + l.gross_credits, 0);
        const typeNetDr  = lines.reduce((s, l) => s + l.net_dr, 0);
        const typeNetCr  = lines.reduce((s, l) => s + l.net_cr, 0);

        return (
          <div key={type} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            {/* Type header */}
            <button
              onClick={() => toggleType(type)}
              className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              data-testid={`type-header-${type}`}
            >
              <div className="flex items-center gap-3">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_STYLES[type]}`}>
                  {typeLabels[type]}
                </span>
                <span className="text-xs text-slate-400">{lines.length} accounts</span>
              </div>
              <div className="flex items-center gap-6 text-xs font-mono text-slate-600">
                <span className="w-28 text-right">{fmt(typeDr)}</span>
                <span className="w-28 text-right">{fmt(typeCr)}</span>
                <span className="w-28 text-right text-slate-900 font-semibold">{fmt(typeNetDr || typeNetCr)}</span>
                {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
            </button>

            {isOpen && (
              <div>
                {/* Column headers */}
                <div className="grid grid-cols-[2rem_1fr_1fr_7rem_7rem_7rem_7rem] gap-0 border-b border-slate-100 bg-white">
                  {['', 'Code', 'Account Name', 'Gross Debits', 'Gross Credits', 'Net Dr', 'Net Cr'].map((h, i) => (
                    <div key={i} className={`px-3 py-2 text-xs font-semibold text-slate-400 ${i >= 3 ? 'text-right' : ''}`}>{h}</div>
                  ))}
                </div>

                {/* Account rows */}
                {lines.map((line, idx) => (
                  <div
                    key={line.account_id}
                    className={`grid grid-cols-[2rem_1fr_1fr_7rem_7rem_7rem_7rem] border-b border-slate-50 last:border-0 hover:bg-slate-50/50 text-sm transition-colors ${idx % 2 === 0 ? '' : 'bg-slate-50/30'}`}
                    data-testid={`trial-line-${line.account_code}`}
                  >
                    <div className="px-3 py-2" />
                    <div className="px-3 py-2 font-mono text-xs text-slate-400">{line.account_code}</div>
                    <div className="px-3 py-2 text-slate-800">{line.account_name}</div>
                    <div className="px-3 py-2 text-right font-mono text-slate-600">{fmt(line.gross_debits)}</div>
                    <div className="px-3 py-2 text-right font-mono text-slate-600">{fmt(line.gross_credits)}</div>
                    <div className="px-3 py-2 text-right font-mono font-medium text-slate-800">{fmt(line.net_dr)}</div>
                    <div className="px-3 py-2 text-right font-mono font-medium text-slate-800">{fmt(line.net_cr)}</div>
                  </div>
                ))}

                {/* Type sub-total */}
                <div className="grid grid-cols-[2rem_1fr_1fr_7rem_7rem_7rem_7rem] bg-slate-50 border-t border-slate-200">
                  <div className="px-3 py-2" />
                  <div className="px-3 py-2 col-span-2 text-xs font-semibold text-slate-500">
                    Total {typeLabels[type]}
                  </div>
                  <div className="px-3 py-2 text-right font-mono text-sm font-bold text-slate-700">{fmt(typeDr)}</div>
                  <div className="px-3 py-2 text-right font-mono text-sm font-bold text-slate-700">{fmt(typeCr)}</div>
                  <div className="px-3 py-2 text-right font-mono text-sm font-bold text-slate-700">{fmt(typeNetDr)}</div>
                  <div className="px-3 py-2 text-right font-mono text-sm font-bold text-slate-700">{fmt(typeNetCr)}</div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Grand Total */}
      {data && data.lines?.length > 0 && (
        <div className="bg-slate-900 text-white rounded-xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-[2rem_1fr_1fr_7rem_7rem_7rem_7rem] px-0">
            <div className="px-3 py-3" />
            <div className="px-3 py-3 col-span-2 text-sm font-bold tracking-wide uppercase">Grand Total</div>
            <div className="px-3 py-3 text-right font-mono text-sm font-bold">{fmt(data.total_gross_dr)}</div>
            <div className="px-3 py-3 text-right font-mono text-sm font-bold">{fmt(data.total_gross_cr)}</div>
            <div className="px-3 py-3 text-right font-mono text-sm font-bold">{fmt(data.total_net_dr)}</div>
            <div className="px-3 py-3 text-right font-mono text-sm font-bold">{fmt(data.total_net_cr)}</div>
          </div>
        </div>
      )}

      {data && data.lines?.length === 0 && !loading && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-400 text-sm">
          No activity found for the selected date range.
        </div>
      )}
    </div>
  );
}
